import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { addWeeks, format } from "date-fns";
import { Pencil } from "lucide-react";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { RichEditor } from "components/RichEditor/RichEditor";
import { Button } from "components/ui/Button";
import { DatePickerField } from "components/ui/DatePickerField/DatePickerField";
import { InsertPickerModal } from "components/ui/InsertPickerModal/InsertPickerModal";
import { Select } from "components/ui/Select/Select";
import { useCreateSickCert } from "features/consultations/hooks/useCreateSickCert";
import { useRefreshSickCertTemplate } from "features/consultations/hooks/useRefreshSickCertTemplate";
import { useReasonForAbsence } from "features/practice/hooks/useReasonForAbsence";
import { useWhatCantBeAttended } from "features/practice/hooks/useWhatCantBeAttended";
import { useLetterTemplates } from "features/templates/hooks/useLetterTemplates";
import { useLetterTemplatesAll } from "features/templates/letter/hooks/useLetterTemplatesAll";
import { selectSickCertTemplates } from "features/templates/utils/sickCertTemplates";
import { sortLetterTemplates } from "features/templates/utils/sortLetterTemplates";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "./AddSickCertForm.module.css";

// TODO: confirm with DEASP whether the limit is 26 or 29 weeks — backend currently returns 29
const SICK_CERT_MAX_WEEKS = 29;

const formSchema = z
  .object({
    name: z.string().min(1, { message: "Please provide a name." }),
    dateFrom: z.string().min(1, { message: "Please select start date." }),
    dateTo: z.string().min(1, { message: "Please select end date." }),
    reasonForAbsence: z
      .string()
      .min(1, { message: "Please provide reason for absence." }),
    whatCantBeAttended: z.string().optional(),
    letterContent: z.string().optional(),
    templateId: z.union([z.number(), z.string(), z.null()]).optional(),
  })
  .superRefine((values, ctx) => {
    if (!values.dateTo) return;

    if (values.dateFrom && new Date(values.dateTo) < new Date(values.dateFrom)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dateTo"],
        message: "End date must be after start date.",
      });
      return;
    }

    const maxDate = addWeeks(new Date(), SICK_CERT_MAX_WEEKS);
    maxDate.setHours(23, 59, 59, 999);

    if (new Date(values.dateTo) > maxDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dateTo"],
        message: `End date cannot be more than ${SICK_CERT_MAX_WEEKS} weeks from today (maximum: ${format(maxDate, "dd/MM/yyyy")}).`,
      });
    }
  });

const getDefaultName = () => `Sick Cert - ${format(new Date(), "dd/MM/yyyy")}`;

const formatCertDate = (value) => {
  if (!value) return "—";

  try {
    return format(new Date(value), "dd MMM yyyy");
  } catch {
    return "—";
  }
};

// Deterministic validity used to gate Save. Required fields present and Date
// To not before Date From. (RHF's formState.isValid proved unreliable here.)
const isSickCertValid = (values) =>
  !!values?.name &&
  !!values?.dateFrom &&
  !!values?.dateTo &&
  !!values?.reasonForAbsence &&
  new Date(values.dateTo) >= new Date(values.dateFrom);

export const AddSickCertForm = ({
  patientId,
  consultationId,
  initialData,
  isReadOnly = false,
  onSuccess,
  onNewSickCert,
  refetchSickCerts,
  showNewCertOption = true,
}) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const savedId = useRef(initialData?.id || null);
  // The backend's UPDATE statement sets document_id from whatever we send —
  // omitting it would null out the link to the already-generated PDF on every
  // save, making the service treat it as "no document yet" and mint a new
  // (orphaned) one each time. Carry it forward across saves so it never gets
  // dropped.
  const savedDocumentId = useRef(initialData?.documentId ?? null);
  const queryClient = useQueryClient();

  const initialContent = initialData?.letterContent || "";

  const [letterContent, setLetterContent] = useState(initialContent);
  const [editorKey, setEditorKey] = useState(0);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");

  // Only the letter/rich-text area gets a read-only-by-default gate — every
  // other field is always directly editable. It starts locked when there's
  // existing letter content to protect (a previously-saved cert), and also
  // (re-)locks whenever a template is applied, so a template's exact wording
  // isn't accidentally hand-edited. The user clicks Edit to unlock it.
  const [isLetterLocked, setIsLetterLocked] = useState(
    !!initialData?.id && !!initialContent
  );
  const [isRefreshingTemplate, setIsRefreshingTemplate] = useState(false);

  const { data: reasonForAbsenceOptions = [] } = useReasonForAbsence();
  const { data: whatCantBeAttendedOptions = [] } = useWhatCantBeAttended();

  // Which template is applied and the exact text we last inserted. The letter is
  // "dirty" when the live content diverges from lastAppliedLetterRef — a baseline
  // that is the applied template and survives saves (so we never overwrite a
  // hand-edited letter). This is deliberately NOT RHF's dirty state, whose
  // baseline is the saved values and would reset on every save.
  // Both refs are seeded from the persisted templateId so that reopening a
  // previously-templated cert (e.g. via EditSickCertModal, after a full
  // remount) still shows the Refresh Template button and doesn't misreport
  // the letter as hand-edited before anything has changed.
  const selectedTemplateKeyRef = useRef(initialData?.templateId ?? null);
  const lastAppliedLetterRef = useRef(initialData?.templateId ? initialContent : null);

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      name: initialData?.name || getDefaultName(),
      dateFrom: initialData?.dateFrom || "",
      dateTo: initialData?.dateTo || "",
      reasonForAbsence: initialData?.reasonForAbsence || "",
      whatCantBeAttended: initialData?.whatCantBeAttended || "",
      letterContent: initialContent,
      templateId: initialData?.templateId ?? null,
    },
  });

  const {
    data: templates = [],
    isLoading: isLoadingTemplates,
  } = useLetterTemplates(patientId, consultationId, { enabled: showTemplateModal });

  /*
   * The same endpoint unscoped, where the `{{sickCert.*}}` placeholders are still intact — the
   * patient-scoped call above substitutes them away, and they are the only thing marking a
   * template as a sick cert (KIN-1954). Cached separately and already warmed by the templates
   * settings table, so this is not a second round trip in practice.
   */
  const { data: rawTemplates = [] } = useLetterTemplatesAll();

  // Only sick-cert templates, then ILH documents first and the rest by name — the picker used
  // to list every letter type in whatever order the endpoint returned (KIN-1954).
  const filteredTemplates = sortLetterTemplates(
    selectSickCertTemplates(templates, rawTemplates).filter((template) =>
      (template.name || "").toLowerCase().includes(templateSearch.toLowerCase())
    )
  );

  const handleOpenTemplateModal = async () => {
    // Sick certs are manual-save-only, but choosing a template must still
    // save first (when the form is valid) so the server substitutes the
    // template's placeholders from the current field values, not stale ones.
    if (isSickCertValid(form.getValues())) {
      await handleFormSubmit(form.getValues());
      // Templates are substituted server-side from the saved cert; drop the
      // cached copy so the modal fetches text reflecting the latest values.
      await queryClient.invalidateQueries({
        queryKey: ["letterTemplates", tenantVal, patientId, consultationId],
      });
    }
    setTemplateSearch("");
    setShowTemplateModal(true);
  };

  const handleSelectTemplate = (template) => {
    const formattedTemplate = template.template
      ? template.template.replace(/\n/g, "<br>")
      : "";
    selectedTemplateKeyRef.current = template.id ?? null;
    lastAppliedLetterRef.current = formattedTemplate;
    setLetterContent(formattedTemplate);
    form.setValue("letterContent", formattedTemplate, { shouldDirty: true });
    form.setValue("templateId", template.id ?? null, { shouldDirty: true });
    setEditorKey((previous) => previous + 1);
    setIsLetterLocked(true);
    setShowTemplateModal(false);
  };

  const createSickCert = useCreateSickCert();
  const createSickCertRef = useRef(createSickCert);
  createSickCertRef.current = createSickCert;

  const refreshSickCertTemplate = useRefreshSickCertTemplate();

  // Reactive validity from the live form values (watch re-renders on change).
  // Gates Save. Also drives the field-level error messages via the resolver
  // (mode: "onChange"), which we still validate for display.
  const watchedValues = form.watch();
  const isFormValid = isSickCertValid(watchedValues);

  // Unsaved changes come straight from RHF — its dirty tracking is exactly this.
  // We rebaseline it (form.reset) after each successful save.
  const { isDirty } = form.formState;

  // Letter is "edited" when it diverges from the template we last applied. Its
  // baseline is the template (not the saved values), so it must be derived here
  // rather than read from RHF's dirty state.
  const isLetterEdited =
    !!selectedTemplateKeyRef.current &&
    watchedValues.letterContent !== (lastAppliedLetterRef.current ?? "");

  // Keep resolver errors (e.g. "End date must be after start date") in sync for
  // display; not used for gating.
  useEffect(() => {
    form.trigger(["dateFrom", "dateTo"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues.dateFrom, watchedValues.dateTo]);

  /*
   * `isUserInitiated` separates the Save the clinician pressed from the silent saves
   * this form makes on its own — opening the template picker saves first so the server
   * substitutes placeholders from current values. Only the deliberate one is worth a
   * toast, and only the deliberate one should close a modal: auto-closing on a
   * background save would shut the form mid-edit.
   */
  const handleFormSubmit = useCallback(
    async (values, { isUserInitiated = false } = {}) => {
      try {
        const response = await createSickCertRef.current.mutateAsync({
          id: savedId.current,
          name: values.name,
          patientId,
          consultationId,
          dateFrom: values.dateFrom,
          dateTo: values.dateTo,
          reasonForAbsence: values.reasonForAbsence,
          whatCantBeAttended: values.whatCantBeAttended,
          letterContent: values.letterContent,
          templateId: values.templateId ?? null,
          // This form has no status control, so preserve whatever the cert's
          // status already was (e.g. don't silently revert an already-SENT
          // cert back to DRAFT on edit) — default to DRAFT only for a
          // brand-new cert.
          status: initialData?.status || "DRAFT",
          documentId: savedDocumentId.current,
          tenant: tenantVal,
        });
        savedId.current = response.id;
        savedDocumentId.current = response.documentId ?? savedDocumentId.current;
        // Rebaseline RHF's dirty state to the just-saved values so isDirty
        // (and the Save button) reflects "no unsaved changes".
        form.reset(form.getValues(), { keepDefaultValues: false });
        refetchSickCerts?.();
        onSuccess?.({ isUserInitiated });
        if (isUserInitiated) {
          toast.success("Sick cert saved");
        }
      } catch (error) {
        // useCreateSickCert already reports the failure, with the server's own
        // reason when it sent one — a second "Failed to save sick cert" here only
        // covered it up.
        console.error("Submission error:", error);
      }
    },
    [
      form,
      consultationId,
      patientId,
      tenantVal,
      onSuccess,
      refetchSickCerts,
      initialData?.status,
    ]
  );

  const handleNewSickCert = () => {
    savedId.current = null;
    savedDocumentId.current = null;
    selectedTemplateKeyRef.current = null;
    lastAppliedLetterRef.current = null;
    setIsLetterLocked(false);
    setLetterContent("");
    form.reset({
      name: getDefaultName(),
      dateFrom: "",
      dateTo: "",
      reasonForAbsence: "",
      whatCantBeAttended: "",
      letterContent: "",
      templateId: null,
    });
    setEditorKey((previous) => previous + 1);
    onNewSickCert?.();
  };

  const handleSaveCert = async () => {
    const isValid = await form.trigger([
      "name",
      "dateFrom",
      "dateTo",
      "reasonForAbsence",
    ]);
    if (!isValid) {
      toast.error("Please fill in all required fields");

      return;
    }
    const values = form.getValues();
    await handleFormSubmit(values, { isUserInitiated: true });
  };

  // Reloads the applied template's text from the server (which re-substitutes
  // it from the currently-saved cert + patient data) and re-locks the editor.
  // Unlike autosave's re-substitution, this always overwrites — it's an
  // explicit user request, so a hand-edited letter is not protected here.
  const hasAppliedTemplate = !!selectedTemplateKeyRef.current;

  const handleRefreshTemplate = async () => {
    if (!selectedTemplateKeyRef.current) return;

    setIsRefreshingTemplate(true);
    try {
      // The refresh endpoint re-substitutes the template server-side from
      // whatever is currently saved against this cert (including its
      // templateId), so make sure the latest field values — and the applied
      // template — are persisted first.
      if (isSickCertValid(form.getValues())) {
        await handleFormSubmit(form.getValues());
      }

      if (!savedId.current) {
        toast.error("Save the sick cert before refreshing its template");
        return;
      }

      const { letterContent: refreshed } = await refreshSickCertTemplate.mutateAsync({
        sickCertId: savedId.current,
        tenantVal,
      });
      const formatted = refreshed ? refreshed.replace(/\n/g, "<br>") : "";

      if (formatted === lastAppliedLetterRef.current) {
        toast.success("Template is already up to date");
        setIsLetterLocked(true);
        return;
      }

      lastAppliedLetterRef.current = formatted;
      setLetterContent(formatted);
      form.setValue("letterContent", formatted, { shouldDirty: true });
      setEditorKey((previous) => previous + 1);
      setIsLetterLocked(true);
      toast.success("Template refreshed with the latest details");
    } catch (error) {
      console.error("Failed to refresh template:", error);
      toast.error("Failed to refresh template");
    } finally {
      setIsRefreshingTemplate(false);
    }
  };

  return (
    <FormProvider {...form}>
      {isReadOnly ? (
        <div className={styles.addSickCertForm}>
          <div className={styles.viewMeta}>
            <span>{initialData?.name || getDefaultName()}</span>
            <span className={styles.viewMetaSep}>·</span>
            <span>From {formatCertDate(initialData?.dateFrom)}</span>
            <span className={styles.viewMetaSep}>·</span>
            <span>To {formatCertDate(initialData?.dateTo)}</span>
          </div>

          <div className={styles.viewBody}>
            <div className={styles.fieldGroup}>
              <span className={styles.fieldLabel}>Reason for Absence</span>
              <p className={styles.fieldValue}>
                {initialData?.reasonForAbsence || "—"}
              </p>
            </div>

            {!!initialData?.whatCantBeAttended && (
              <div className={styles.fieldGroup}>
                <span className={styles.fieldLabel}>What can't be attended</span>
                <p className={styles.fieldValue}>
                  {initialData.whatCantBeAttended}
                </p>
              </div>
            )}

            {!!initialContent && (
              <div className={styles.fieldGroup}>
                <span className={styles.fieldLabel}>Certificate Content</span>
                <iframe
                  className={styles.letterFrame}
                  srcDoc={initialContent}
                  title="Certificate content"
                  sandbox="allow-same-origin"
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <form
          className={styles.addSickCertForm}
          onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("AddSickCertForm"))}
        >
          <div className={styles.inputGroupGrid}>
            <FormField name="name" label="Name *">
              <input
                type="text"
                className={styles.formInput}
                {...form.register("name")}
              />
            </FormField>

            <FormField name="dateFrom" label="Date From *">
              <DatePickerField
                value={form.watch("dateFrom")}
                onChange={(value) =>
                  form.setValue("dateFrom", value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                placeholder="Select start date"
              />
            </FormField>

            <FormField name="dateTo" label="Date To *">
              <DatePickerField
                value={form.watch("dateTo")}
                onChange={(value) =>
                  form.setValue("dateTo", value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                placeholder="Select end date"
                minDate={
                  form.watch("dateFrom") ? new Date(form.watch("dateFrom")) : null
                }
              />
            </FormField>
          </div>

          <div className={styles.inputGroupGrid2}>
            <FormField name="reasonForAbsence" label="Reason for absence *">
              <Select
                name="reasonForAbsence"
                options={reasonForAbsenceOptions}
                value={form.watch("reasonForAbsence")}
                onChange={(value) =>
                  form.setValue("reasonForAbsence", value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                placeholder="Select reason for absence"
                ariaRequired
                fullWidth
              />
            </FormField>

            <FormField name="whatCantBeAttended" label="What can't be attended">
              <Select
                name="whatCantBeAttended"
                options={whatCantBeAttendedOptions}
                value={form.watch("whatCantBeAttended")}
                onChange={(value) =>
                  form.setValue("whatCantBeAttended", value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                placeholder="Select what can't be attended"
                fullWidth
              />
            </FormField>
          </div>

          <FormField name="letterContent">
            <div className={styles.letterHeader}>
              <Button
                type="button"
                variant="outline"
                size="small"
                onClick={handleOpenTemplateModal}
                disabled={!isFormValid}
              >
                Choose Template
              </Button>

              {hasAppliedTemplate && (
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={handleRefreshTemplate}
                  disabled={isRefreshingTemplate}
                >
                  {isRefreshingTemplate ? "Refreshing..." : "Refresh Template"}
                </Button>
              )}

              {isLetterLocked && (
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  startIcon={Pencil}
                  onClick={() => setIsLetterLocked(false)}
                >
                  Edit
                </Button>
              )}

              {!isFormValid && (
                <p className={styles.saveCertHint}>
                  Complete the required fields and a valid date range to use
                  templates
                </p>
              )}
            </div>

            {isLetterEdited && (
              <p className={styles.letterWarning}>
                You've edited this letter, so it won't auto-update when you change
                the form. Click Choose Template or Refresh Template to replace it
                with the latest details.
              </p>
            )}

            <RichEditor
              key={editorKey}
              editorValue={letterContent}
              onChange={(value) => {
                if (isLetterLocked) return;
                setLetterContent(value);
                form.setValue("letterContent", value, { shouldDirty: true });
              }}
              readOnly={isLetterLocked}
            />
          </FormField>

          <div className={styles.bottomSection}>
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveCert}
              disabled={createSickCert.isPending || !isFormValid || !isDirty}
            >
              {createSickCert.isPending ? "Saving..." : "Save"}
            </Button>

            {showNewCertOption && (
              <Button
                type="button"
                variant="outline"
                onClick={form.handleSubmit(async (values) => {
                  await handleFormSubmit(values);
                  handleNewSickCert();
                }, onInvalidSubmit("AddSickCertForm"))}
              >
                New Sick Cert
              </Button>
            )}
          </div>
        </form>
      )}

      <InsertPickerModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title="Select Template"
        search={templateSearch}
        onSearchChange={setTemplateSearch}
        searchPlaceholder="Search templates..."
        isLoading={isLoadingTemplates}
        loadingText="Loading templates..."
        emptyText="No templates found"
        items={filteredTemplates}
        onSelect={handleSelectTemplate}
        renderItem={(template) => (
          <div className={styles.templateItem}>{template.name}</div>
        )}
      />
    </FormProvider>
  );
};
